import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Save, Edit } from 'lucide-react'
import { useState } from 'react'
import type { EnemyResult } from '@creanizer/types'
import { EnemyEditModal } from './EnemyEditModal'

interface EnemyResultProps {
  result: EnemyResult
  onSave?: () => void
  isSaved?: boolean
  onEdit?: (updatedResult: Partial<EnemyResult>) => void
}

export function EnemyResult({ result, onSave, isSaved, onEdit }: EnemyResultProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  
  if (!result || !result.basicInfo) {
    return (
      <div className="card">
        <div className="py-8">
          <p className="text-center text-muted-foreground">生成結果が正しく取得できませんでした</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Tabs defaultValue="basic" className="w-full">
      <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 gap-1">
        <TabsTrigger value="basic">基本情報</TabsTrigger>
        <TabsTrigger value="appearance">外見</TabsTrigger>
        <TabsTrigger value="abilities">能力</TabsTrigger>
        <TabsTrigger value="behavior">行動</TabsTrigger>
        <TabsTrigger value="lore">伝承</TabsTrigger>
        <TabsTrigger value="encounter">遭遇</TabsTrigger>
      </TabsList>

      <TabsContent value="basic">
        <div className="card">
          <div className="p-6 flex justify-between items-start">
            <div>
              <h3 className="text-lg font-semibold">{result.basicInfo?.name || '名前なし'}</h3>
              {result.basicInfo?.title && (
                <p className="text-sm text-muted-foreground text-lg">{result.basicInfo.title}</p>
              )}
            </div>
            <div className="flex gap-2">
              {onSave && (
                <Button
                  size="sm"
                  variant={isSaved ? 'secondary' : 'outline'}
                  onClick={onSave}
                  disabled={isSaved}
                >
                  <Save className="h-4 w-4 mr-1" />
                  {isSaved ? '保存済み' : '保存'}
                </Button>
              )}
              {onEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsEditModalOpen(true)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  編集
                </Button>
              )}
            </div>
          </div>
          <div className="px-6 pb-6 space-y-4">
            <div className="flex gap-2">
              {result.basicInfo?.type && <Badge>{result.basicInfo.type}</Badge>}
              {result.basicInfo?.category && <Badge variant="secondary">{result.basicInfo.category}</Badge>}
              {result.basicInfo?.threat && <Badge variant="destructive">脅威度: {result.basicInfo.threat}</Badge>}
            </div>
            
            {result.basicInfo?.summary && (
              <div>
                <h4 className="font-semibold mb-2">概要</h4>
                <p className="text-sm">{result.basicInfo.summary}</p>
              </div>
            )}

            <hr className="my-4" />

            {result.basicInfo?.description && (
              <div>
                <h4 className="font-semibold mb-2">詳細説明</h4>
                <p className="text-sm whitespace-pre-wrap">{result.basicInfo.description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {result.basicInfo?.habitat && (
                <div>
                  <h4 className="font-semibold mb-2">生息地</h4>
                  <p className="text-sm">{result.basicInfo.habitat}</p>
                </div>
              )}
              {result.basicInfo?.timeOfActivity && (
                <div>
                  <h4 className="font-semibold mb-2">活動時間</h4>
                  <p className="text-sm">{result.basicInfo.timeOfActivity}</p>
                </div>
              )}
              {result.basicInfo.distribution && (
                <div>
                  <h4 className="font-semibold mb-2">分布</h4>
                  <p className="text-sm">{result.basicInfo.distribution}</p>
                </div>
              )}
              {result.basicInfo.frequency && (
                <div>
                  <h4 className="font-semibold mb-2">遭遇頻度</h4>
                  <p className="text-sm">{result.basicInfo.frequency}</p>
                </div>
              )}
            </div>

            {result.basicInfo.aliases && result.basicInfo.aliases.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">別名</h4>
                <div className="flex flex-wrap gap-2">
                  {result.basicInfo.aliases.map((alias, i) => (
                    <Badge key={i} variant="outline">{alias}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="appearance">
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-semibold">外見</h3>
          </div>
          <div className="px-6 pb-6 space-y-4">
            {result.appearance && (
              <>
                <div>
                  <h4 className="font-semibold mb-2">全体的な外見</h4>
                  <p className="text-sm whitespace-pre-wrap">{result.appearance.overallDescription || '不明'}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">サイズ</h4>
                    <p className="text-sm">{result.appearance.size || '不明'}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">体型</h4>
                    <p className="text-sm">{result.appearance.bodyType || '不明'}</p>
                  </div>
                  {result.appearance.height && (
                    <div>
                      <h4 className="font-semibold mb-2">高さ</h4>
                      <p className="text-sm">{result.appearance.height}</p>
                    </div>
                  )}
                  {result.appearance.weight && (
                    <div>
                      <h4 className="font-semibold mb-2">重さ</h4>
                      <p className="text-sm">{result.appearance.weight}</p>
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="font-semibold mb-2">色彩</h4>
                  <p className="text-sm">{result.appearance.colorScheme || '不明'}</p>
                </div>

                {result.appearance.distinctiveFeatures && result.appearance.distinctiveFeatures.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">特徴的な部位</h4>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {result.appearance.distinctiveFeatures.map((feature, i) => (
                        <li key={i}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <h4 className="font-semibold mb-2">視覚的インパクト</h4>
                  <p className="text-sm">{result.appearance.visualImpact || '不明'}</p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">初見の恐怖度</h4>
                  <p className="text-sm">{result.appearance.firstImpressionFear || '不明'}</p>
                </div>

                {result.appearance.sounds && result.appearance.sounds.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">発する音</h4>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {result.appearance.sounds.map((sound, i) => (
                        <li key={i}>{sound}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="abilities">
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-semibold">能力</h3>
          </div>
          <div className="px-6 pb-6 space-y-4">
            {result.abilities?.combatCapabilities?.attackPatterns && result.abilities.combatCapabilities.attackPatterns.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">攻撃パターン</h4>
                <div className="space-y-3">
                  {result.abilities.combatCapabilities.attackPatterns.map((attack, i) => (
                    <div key={i} className="border rounded-lg p-3">
                      <h5 className="font-medium">{attack.name}</h5>
                      <p className="text-sm text-muted-foreground mt-1">{attack.description}</p>
                      <div className="flex gap-4 mt-2 text-xs">
                        <span>ダメージ: {attack.damage}</span>
                        <span>範囲: {attack.range}</span>
                        <span>頻度: {attack.frequency}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.abilities?.combatCapabilities?.specialAbilities && result.abilities.combatCapabilities.specialAbilities.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">特殊能力</h4>
                <div className="space-y-3">
                  {result.abilities.combatCapabilities.specialAbilities.map((ability, i) => (
                    <div key={i} className="border rounded-lg p-3">
                      <h5 className="font-medium">{ability.name}</h5>
                      <p className="text-sm text-muted-foreground mt-1">{ability.description}</p>
                      <p className="text-sm mt-1">効果: {ability.effect}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.abilities?.weaknesses && result.abilities.weaknesses.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">弱点</h4>
                <div className="space-y-2">
                  {result.abilities.weaknesses.map((weakness, i) => (
                    <div key={i} className="border-l-4 border-green-500 pl-3">
                      <h5 className="font-medium text-sm">{weakness.type}</h5>
                      <p className="text-sm text-muted-foreground">{weakness.description}</p>
                      <p className="text-xs mt-1">効果: {weakness.effectiveness}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.abilities?.resistances && result.abilities.resistances.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">耐性</h4>
                <div className="space-y-2">
                  {result.abilities.resistances.map((resistance, i) => (
                    <div key={i} className="border-l-4 border-red-500 pl-3">
                      <h5 className="font-medium text-sm">{resistance.type}</h5>
                      <p className="text-sm text-muted-foreground">{resistance.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="behavior">
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-semibold">行動パターン</h3>
          </div>
          <div className="px-6 pb-6 space-y-4">
            {result.behavior ? (
              <>
                <div>
                  <h4 className="font-semibold mb-2">AI行動パターン</h4>
                  <p className="text-sm">{result.behavior.aiPattern || '不明'}</p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">知能説明</h4>
                  <p className="text-sm">{result.behavior.intelligenceDescription || '不明'}</p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">狩りのパターン</h4>
                  <p className="text-sm">{result.behavior.huntingPattern || '不明'}</p>
                </div>

                {result.behavior.triggers && (
                  <div>
                    <h4 className="font-semibold mb-2">行動トリガー</h4>
                    <div className="space-y-2">
                      {result.behavior.triggers.aggressive && result.behavior.triggers.aggressive.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium">攻撃的になる条件</h5>
                          <ul className="list-disc list-inside text-sm text-muted-foreground">
                            {result.behavior.triggers.aggressive.map((trigger, i) => (
                              <li key={i}>{trigger}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {result.behavior.triggers.defensive && result.behavior.triggers.defensive.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium">防御的になる条件</h5>
                          <ul className="list-disc list-inside text-sm text-muted-foreground">
                            {result.behavior.triggers.defensive.map((trigger, i) => (
                              <li key={i}>{trigger}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {result.behavior.triggers.flee && result.behavior.triggers.flee.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium">逃走条件</h5>
                          <ul className="list-disc list-inside text-sm text-muted-foreground">
                            {result.behavior.triggers.flee.map((trigger, i) => (
                              <li key={i}>{trigger}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {result.behavior.phases && result.behavior.phases.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">戦闘フェーズ</h4>
                    <div className="space-y-3">
                      {result.behavior.phases.map((phase, i) => (
                        <div key={i} className="border rounded-lg p-3">
                          <h5 className="font-medium">フェーズ {phase.phase}: {phase.name}</h5>
                          <p className="text-sm text-muted-foreground mt-1">トリガー: {phase.trigger}</p>
                          <p className="text-sm mt-1">{phase.description}</p>
                          <Badge variant="destructive" className="mt-2">危険度: {phase.dangerLevel}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-center text-muted-foreground">行動データがありません</p>
            )}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="lore">
        <ScrollArea className="h-[600px]">
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-semibold">伝承と物語</h3>
            </div>
            <div className="px-6 pb-6 space-y-6">
              {result.lore ? (
                <>
                  {result.lore.originStory && (
                    <div>
                      <h4 className="font-semibold mb-2">起源物語</h4>
                      <div className="bg-muted rounded-lg p-4">
                        <div className="flex gap-4 mb-2 text-sm">
                          <span>時代: {result.lore.originStory.era || '不明'}</span>
                          <span>場所: {result.lore.originStory.location || '不明'}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">
                          {result.lore.originStory.narrative || '詳細不明'}
                        </p>
                        {result.lore.originStory.variations && result.lore.originStory.variations.length > 0 && (
                          <div className="mt-3">
                            <h5 className="text-sm font-medium mb-1">異説</h5>
                            <ul className="list-disc list-inside text-sm text-muted-foreground">
                              {result.lore.originStory.variations.map((variation, i) => (
                                <li key={i}>{variation}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {result.lore.legends && result.lore.legends.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">伝説</h4>
                      <div className="space-y-4">
                        {result.lore.legends.map((legend, i) => (
                          <div key={i} className="border rounded-lg p-4">
                            <h5 className="font-medium text-lg mb-2">{legend.title}</h5>
                            <div className="flex gap-4 mb-2 text-sm text-muted-foreground">
                              <span>地域: {legend.region}</span>
                              <span>時代: {legend.period}</span>
                              <span>信憑性: {legend.reliability}</span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap leading-relaxed">
                              {legend.story}
                            </p>
                            {legend.moral && (
                              <div className="mt-3 p-2 bg-muted rounded">
                                <p className="text-sm font-medium">教訓: {legend.moral}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.lore.sightings && result.lore.sightings.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">目撃証言</h4>
                      <div className="space-y-3">
                        {result.lore.sightings.map((sighting, i) => (
                          <div key={i} className="border-l-4 border-yellow-500 pl-4">
                            <div className="flex justify-between items-start mb-1">
                              <span className="text-sm font-medium">{sighting.date} - {sighting.location}</span>
                              <Badge variant="outline" className="text-xs">{sighting.credibility}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-1">目撃者: {sighting.witness}</p>
                            <p className="text-sm">{sighting.description}</p>
                            {sighting.aftermath && (
                              <p className="text-sm mt-2 text-muted-foreground">その後: {sighting.aftermath}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.lore.rumors && result.lore.rumors.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">噂話</h4>
                      <div className="space-y-2">
                        {result.lore.rumors.map((rumor, i) => (
                          <div key={i} className="bg-muted rounded-lg p-3">
                            <p className="text-sm italic">「{rumor.content}」</p>
                            <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                              <span>広まり方: {rumor.spreadMethod}</span>
                              <span>信憑性: {rumor.believability}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-center text-muted-foreground">伝承データがありません</p>
              )}
            </div>
          </div>
        </ScrollArea>
      </TabsContent>

      <TabsContent value="encounter">
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-semibold">遭遇情報</h3>
          </div>
          <div className="px-6 pb-6 space-y-4">
            {result.encounter ? (
              <>
                {result.encounter.conditions && (
                  <div>
                    <h4 className="font-semibold mb-2">遭遇条件</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {result.encounter.conditions.location && result.encounter.conditions.location.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium mb-1">場所</h5>
                          <ul className="list-disc list-inside text-sm text-muted-foreground">
                            {result.encounter.conditions.location.map((loc, i) => (
                              <li key={i}>{loc}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {result.encounter.conditions.time && result.encounter.conditions.time.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium mb-1">時間</h5>
                          <ul className="list-disc list-inside text-sm text-muted-foreground">
                            {result.encounter.conditions.time.map((time, i) => (
                              <li key={i}>{time}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {result.encounter.omens && (
                  <div>
                    <h4 className="font-semibold mb-2">前兆</h4>
                    {result.encounter.omens.sensory && (
                      <div className="space-y-2">
                        {result.encounter.omens.sensory.sight && result.encounter.omens.sensory.sight.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium">視覚</h5>
                            <ul className="list-disc list-inside text-sm text-muted-foreground">
                              {result.encounter.omens.sensory.sight.map((omen, i) => (
                                <li key={i}>{omen}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {result.encounter.omens.sensory.sound && result.encounter.omens.sensory.sound.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium">聴覚</h5>
                            <ul className="list-disc list-inside text-sm text-muted-foreground">
                              {result.encounter.omens.sensory.sound.map((omen, i) => (
                                <li key={i}>{omen}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {result.encounter.taboos && result.encounter.taboos.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">してはいけないこと</h4>
                    <div className="space-y-2">
                      {result.encounter.taboos.map((taboo, i) => (
                        <div key={i} className="border border-red-500 rounded-lg p-3">
                          <h5 className="font-medium text-red-700">{taboo.action}</h5>
                          <p className="text-sm mt-1">{taboo.consequence}</p>
                          <Badge variant="destructive" className="mt-2">重大度: {taboo.severity}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {result.encounter.scenarios && result.encounter.scenarios.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">遭遇シナリオ</h4>
                    <div className="space-y-3">
                      {result.encounter.scenarios.map((scenario, i) => (
                        <div key={i} className="border rounded-lg p-3">
                          <h5 className="font-medium">{scenario.situation}</h5>
                          <p className="text-sm text-muted-foreground mt-1">{scenario.description}</p>
                          {scenario.escapeMethod && (
                            <p className="text-sm mt-2">脱出方法: {scenario.escapeMethod}</p>
                          )}
                          <Badge className="mt-2">生存率: {scenario.survivalRate}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-center text-muted-foreground">遭遇データがありません</p>
            )}
          </div>
        </div>
      </TabsContent>
      </Tabs>
      
      {onEdit && (
        <EnemyEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={(updatedResult) => {
            onEdit(updatedResult)
            setIsEditModalOpen(false)
          }}
          enemyResult={result}
          title="エネミー設定を編集"
        />
      )}
    </>
  )
}